import { useGetUserQuery } from 'entities/users'
import { useEffect } from 'react'
import { isMobile, isTablet } from 'react-device-detect'
import { useMediaQuery } from 'react-responsive'
import {
    LocalStorageKeys,
    env,
    useAppDispatch,
    useAppSelector,
    useLocalStorage,
} from 'shared/lib'

/**
 * Custom hook for handling header logic, including theme, user profile, and responsive design.
 *
 * @returns {object} - An object containing various state and dispatch functions for the header component.
 * @returns {boolean} isMobile - Indicates if the device is a mobile device.
 * @returns {boolean} isTablet - Indicates if the device is a tablet.
 * @returns {boolean} isBigScreen - Indicates if the screen width is 1023px or greater.
 * @returns {boolean} isDarkMode - Indicates if the dark mode is enabled.
 * @returns {boolean} isShowPicture - Indicates if the profile picture should be shown.
 * @returns {boolean} showPicture - Parsed value from local storage indicating if the profile picture should be shown.
 * @returns {string} avatarImg - The URL of the user's avatar image.
 * @returns {Function} dispatch - Redux dispatch function for triggering actions.
 */
export const useHeader = () => {
    const { data } = useGetUserQuery('')

    const isBigScreen = useMediaQuery({ query: '(min-width: 1023px)' })
    const dispatch = useAppDispatch()
    const isDarkMode = useAppSelector((state) => state?.globalReducer.darkMode)

    const { getItem } = useLocalStorage()

    const isProfileImage = getItem(LocalStorageKeys.SHOW_PROFILE_PICTURE)

    const isShowPicture = useAppSelector(
        (state) => state?.globalReducer.showImage
    )
    const showPicture = isProfileImage ? JSON.parse(isProfileImage) : false

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark')
            return
        }

        document.documentElement.classList.remove('dark')
    }, [isDarkMode])
    const imageKey = data?.imageKey
    const avatarImg = `${env.awsUrl}${imageKey}`

    return {
        isMobile,
        isTablet,
        isBigScreen,
        isDarkMode,
        isShowPicture,
        showPicture,
        avatarImg,
        dispatch,
    }
}
